import React, { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Trophy, BarChart3, Target, Play, Crown, ListOrdered, Gamepad2, Filter, X, ChevronLeft, ChevronRight, RotateCcw, Coins } from 'lucide-react'
import Footer from '../components/Footer'
import FootballRoomDemo from '../components/demo/FootballRoomDemo'
import CreateRoomModal from '../components/CreateRoomModal'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import type { DemoRoomConfig } from '../demo/demoRoomTypes'
import {
  loadDemoRoomSession,
  saveDemoRoomSession,
  clearDemoRoomSession,
  DEFAULT_DEMO_ROOM_CONFIG,
} from '../demo/demoRoomTypes'

const btnGreen =
  'inline-flex items-center justify-center gap-1.5 sm:gap-2 px-4 py-2.5 sm:px-5 sm:py-3 rounded-lg sm:rounded-xl bg-green-800 text-white text-small sm:text-nav font-semibold hover:bg-green-700 transition-all'

// Mock users (international names in English, same on all locales)
const MOCK_FOOTBALL_PUBLIC = [
  { rank: 1, name: 'Alex Rivera', points: 2840, accuracy: 72, matches: 42 },
  { rank: 2, name: 'Sam Chen', points: 2690, accuracy: 68, matches: 40 },
  { rank: 3, name: 'Jordan Lee', points: 2510, accuracy: 65, matches: 38 },
  { rank: 4, name: 'Morgan Taylor', points: 2380, accuracy: 63, matches: 41 },
  { rank: 5, name: 'Casey Brown', points: 2210, accuracy: 61, matches: 39 },
]

const MOCK_F1_PUBLIC = [
  { rank: 1, name: 'Alex Rivera', points: 3120, accuracy: 58, races: 18 },
  { rank: 2, name: 'Sam Chen', points: 2890, accuracy: 55, races: 18 },
  { rank: 3, name: 'Jordan Lee', points: 2650, accuracy: 52, races: 17 },
  { rank: 4, name: 'Morgan Taylor', points: 2410, accuracy: 50, races: 18 },
  { rank: 5, name: 'Casey Brown', points: 2180, accuracy: 48, races: 17 },
]

const MOCK_F1_ROOM = [
  { rank: 1, name: 'Jamie Fox', points: 186, predCount: 6 },
  { rank: 2, name: 'Riley Davis', points: 172, predCount: 6 },
  { rank: 3, name: 'Quinn Miller', points: 158, predCount: 6 },
  { rank: 4, name: 'Avery Clark', points: 145, predCount: 5 },
  { rank: 5, name: 'Skyler White', points: 132, predCount: 5 },
  { rank: 6, name: 'Morgan Hayes', points: 118, predCount: 5 },
  { rank: 7, name: 'Drew Bennett', points: 104, predCount: 5 },
  { rank: 8, name: 'Jordan Blake', points: 92, predCount: 4 },
  { rank: 9, name: 'Taylor Reed', points: 78, predCount: 4 },
]

// F1 demo: races list (Races and Predictions modal)
const DEMO_F1_RACES = [
  { id: '1', day: 1, city: 'Melbourne', country: 'Australia', date: '3/6/2026', drivers: 22, status: 'completed' as const, resultsAvailable: true },
  { id: '2', day: 2, city: 'Shanghai', country: 'China', date: '3/13/2026', drivers: 22, status: 'upcoming' as const, resultsAvailable: false },
  { id: '3', day: 3, city: 'Suzuka', country: 'Japan', date: '3/20/2026', drivers: 22, status: 'upcoming' as const, resultsAvailable: false },
  { id: '4', day: 4, city: 'Monaco', country: 'Monaco', date: '3/27/2026', drivers: 22, status: 'upcoming' as const, resultsAvailable: false },
]

// F1 demo: 22 mock drivers (English names) for Play modal and Games modal
const DEMO_F1_DRIVERS = [
  'James Wilson', 'Marcus Cole', 'Daniel Brooks', 'Chris Evans', 'Ryan Scott',
  'Kevin Moore', 'Brandon Lee', 'Nathan Hill', 'Greg Foster', 'Sean Phillips',
  'Adam Turner', 'Luke Bennett', 'Jake Mitchell', 'Ethan Clark', 'Owen Wright',
  'Caleb Hughes', 'Dylan Green', 'Tyler Adams', 'Blake Parker', 'Jordan Fisher',
  'Alex Morgan', 'Sam Cooper',
]

// F1 demo: Best of scored predictions (mock)
const DEMO_F1_BEST_OF = [
  { playerName: 'Jamie Fox', raceDay: 1, raceName: 'Melbourne, Australia', raceDate: 'Mar 6, 2026', predictedText: '1. James Wilson, 2. Marcus Cole, 3. Daniel Brooks, 4. Chris Evans, 5. Ryan Scott, 6. Kevin Moore, 7. Brandon Lee, 8. Nathan Hill, 9. Greg Foster, 10. Sean Phillips', pts: 28 },
  { playerName: 'Riley Davis', raceDay: 1, raceName: 'Melbourne, Australia', raceDate: 'Mar 6, 2026', predictedText: '1. Marcus Cole, 2. James Wilson, 3. Daniel Brooks, 4. Ryan Scott, 5. Chris Evans, 6. Kevin Moore, 7. Brandon Lee, 8. Nathan Hill, 9. Greg Foster, 10. Sean Phillips', pts: 22 },
]

// F1 demo: player Games modal – races with P1–P10 predictions (mock driver names)
const DEMO_F1_PLAYER_RACES = [
  { id: '2', day: 2, city: 'Shanghai', country: 'China', date: 'Mar 13, 2026', status: 'upcoming' as const, pts: 0, positions: ['James Wilson', 'Marcus Cole', 'Daniel Brooks', 'Chris Evans', 'Ryan Scott', 'Kevin Moore', 'Brandon Lee', 'Nathan Hill', 'Greg Foster', 'Sean Phillips'] },
  { id: '1', day: 1, city: 'Melbourne', country: 'Australia', date: 'Mar 6, 2026', status: 'completed' as const, pts: 28, positions: ['James Wilson', 'Marcus Cole', 'Daniel Brooks', 'Chris Evans', 'Ryan Scott', 'Kevin Moore', 'Brandon Lee', 'Nathan Hill', 'Greg Foster', 'Sean Phillips'] },
]

type DemoPlayer = { rank: number; name: string; points: number; predCount: number }

const Showcase: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'football' | 'formula1'>(
    searchParams.get('football') === '1' ? 'football' : 'football',
  )
  const [demoRoomConfig, setDemoRoomConfig] = useState<DemoRoomConfig | null>(() => loadDemoRoomSession())
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAdminDemo, setShowAdminDemo] = useState(false)
  const roomSectionRef = useRef<HTMLDivElement>(null)
  const adminSectionRef = useRef<HTMLDivElement>(null)
  const [demoF1PlayOpen, setDemoF1PlayOpen] = useState(false)
  const [demoF1BestOfOpen, setDemoF1BestOfOpen] = useState(false)
  const [demoF1RacesListOpen, setDemoF1RacesListOpen] = useState(false)
  const [demoF1GamesPlayer, setDemoF1GamesPlayer] = useState<DemoPlayer | null>(null)
  const [demoF1StatsPlayer, setDemoF1StatsPlayer] = useState<DemoPlayer | null>(null)
  const [demoF1RaceDetail, setDemoF1RaceDetail] = useState<typeof DEMO_F1_RACES[0] | null>(null)
  const [comingSoonOpen, setComingSoonOpen] = useState(false)
  const [comingSoonKind, setComingSoonKind] = useState<'golf' | 'tennis' | null>(null)

  const tabs = [
    { id: 'football' as const, labelKey: 'predictionsPage.football', emoji: '⚽' },
    { id: 'formula1' as const, labelKey: 'predictionsPage.formula1', emoji: '🏎️' },
  ]

  const comingSoonTabs = [
    { id: 'golf' as const, labelKey: 'predictionsPage.virtualGolf', emoji: '⛳' },
    { id: 'tennis' as const, labelKey: 'predictionsPage.tennis', emoji: '🎾' },
  ]

  useEffect(() => {
    if (!comingSoonOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setComingSoonOpen(false)
        setComingSoonKind(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [comingSoonOpen])

  useEffect(() => {
    if (!comingSoonOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [comingSoonOpen])

  const isFootball = activeTab === 'football'
  const publicData = isFootball ? MOCK_FOOTBALL_PUBLIC : MOCK_F1_PUBLIC
  const roomData = isFootball ? [] : MOCK_F1_ROOM
  const metricsLabel = isFootball ? t('predictionsPage.matchesPlayed') : t('predictionsPage.races')

  const handleDemoRoomCreated = (config: DemoRoomConfig) => {
    setDemoRoomConfig(config)
    saveDemoRoomSession(config)
    setShowCreateModal(false)
    window.setTimeout(() => {
      roomSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 350)
  }

  const resetDemo = () => {
    clearDemoRoomSession()
    setDemoRoomConfig(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const skipToDemoRoom = () => {
    handleDemoRoomCreated(DEFAULT_DEMO_ROOM_CONFIG)
  }

  const revealAdminDemo = () => {
    setShowAdminDemo(true)
    window.setTimeout(() => {
      adminSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 120)
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-background" data-page="predictions">
      {/* Hero */}
      <section className="pt-24 sm:pt-28 pb-8 gradient-bg">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-display text-display-1 font-bold text-foreground mb-4"
          >
            📊 {t('predictionsPage.title')}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="max-w-2xl mx-auto mb-8 space-y-3 sm:space-y-4 flex flex-col items-center"
          >
            <p className="font-display inline-flex items-center justify-center gap-2 sm:gap-3 text-[clamp(1.125rem,1.5vw+0.9rem,1.5rem)] sm:text-[1.375rem] font-semibold leading-snug px-4 py-3 sm:px-6 sm:py-3.5 rounded-2xl bg-primary/15 text-primary border border-primary/20 shadow-sm w-full sm:w-auto [text-shadow:0_0_1px_rgba(255,255,255,0.3)]">
              <span className="text-2xl sm:text-3xl shrink-0">🌍</span>
              <span>{t('predictionsPage.subtitleLine1')}</span>
            </p>
            <p className="font-display inline-flex items-center justify-center gap-2 sm:gap-3 text-[clamp(1.125rem,1.5vw+0.9rem,1.5rem)] sm:text-[1.375rem] font-semibold leading-snug px-4 py-3 sm:px-6 sm:py-3.5 rounded-2xl bg-success/15 text-success border border-success/25 shadow-sm w-full sm:w-auto [text-shadow:0_0_1px_rgba(255,255,255,0.3)]">
              <span className="text-2xl sm:text-3xl shrink-0">📊</span>
              <span>{t('predictionsPage.subtitleLine2')}</span>
            </p>
          </motion.div>

          {/* Tabs: Football | Formula 1 | Virtual golf | Tennis (coming soon) */}
          <div className="inline-flex gap-2 sm:gap-3 flex-wrap justify-center">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-xl text-nav font-medium transition-all flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'glass hover:bg-muted/20 text-muted-foreground'
                }`}
              >
                <span>{tab.emoji}</span>
                {t(tab.labelKey)}
              </button>
            ))}
            {comingSoonTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setComingSoonKind(tab.id)
                  setComingSoonOpen(true)
                }}
                className="px-5 py-2.5 rounded-xl text-nav font-medium transition-all flex items-center gap-2 glass hover:bg-muted/20 text-muted-foreground border border-dashed border-primary/35 hover:border-primary/50"
              >
                <span>{tab.emoji}</span>
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {comingSoonOpen && (
          <motion.div
            key="coming-soon-predictions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:items-start md:pt-[5.75rem] md:pb-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="coming-soon-title"
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/60 backdrop-blur-md border-0 p-0 w-full h-full cursor-default"
              aria-label={t('predictionsPage.comingSoonCloseBackdrop')}
              onClick={() => {
                setComingSoonOpen(false)
                setComingSoonKind(null)
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="relative z-10 w-full max-w-md rounded-2xl sm:rounded-3xl border border-white/20 bg-slate-900/75 backdrop-blur-xl shadow-2xl p-6 sm:p-8 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  setComingSoonOpen(false)
                  setComingSoonKind(null)
                }}
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/40 hover:bg-black/55 border border-white/15 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={t('predictionsPage.comingSoonClose')}
              >
                <X className="w-5 h-5" />
              </button>
              <div className="text-4xl sm:text-5xl mb-4" aria-hidden>
                {comingSoonKind === 'golf' ? '⛳' : '🎾'}
              </div>
              <h2 id="coming-soon-title" className="font-display text-xl sm:text-2xl font-bold text-foreground mb-3 pr-8">
                {t('predictionsPage.comingSoon')}
              </h2>
              <p className="text-body-lg text-muted-foreground leading-relaxed">
                {comingSoonKind === 'golf'
                  ? t('predictionsPage.comingSoonGolfBody')
                  : t('predictionsPage.comingSoonTennisBody')}
              </p>
              <button
                type="button"
                onClick={() => {
                  setComingSoonOpen(false)
                  setComingSoonKind(null)
                }}
                className="mt-6 w-full sm:w-auto px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
              >
                {t('predictionsPage.comingSoonOk')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content: Public + Room leaderboards with mock data */}
      <section className="py-8 sm:py-12 pb-20">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-8 sm:space-y-10"
            >
              {isFootball && (
                <div className="rounded-2xl border border-primary/20 bg-card overflow-hidden shadow-lg shadow-primary/5">
                  <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
                          🏆 {t('predictionsPage.demoWorldCupTitle')}
                        </h2>
                        <span className="text-xs font-medium text-primary px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                          {t('predictionsPage.demoNoSignIn')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{t('predictionsPage.demoWorldCupSubtitle')}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                      <button type="button" onClick={() => setShowCreateModal(true)} className={btnGreen}>
                        {t('dashboard.createRoom')}
                      </button>
                      <button
                        type="button"
                        onClick={revealAdminDemo}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 sm:px-5 sm:py-3 rounded-lg sm:rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300 text-small sm:text-nav font-semibold hover:bg-amber-500/20 transition-all"
                      >
                        <Coins size={16} /> {t('predictionsPage.seeAdminFeatures')}
                      </button>
                      {demoRoomConfig && (
                        <button
                          type="button"
                          onClick={resetDemo}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg sm:rounded-xl border border-border text-small sm:text-nav font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                        >
                          <RotateCcw size={16} /> {t('predictionsPage.demoReset')}
                        </button>
                      )}
                    </div>
                  </div>
                  {!demoRoomConfig && (
                    <p className="px-4 sm:px-6 pb-4 text-xs text-muted-foreground/70">
                      <button type="button" onClick={skipToDemoRoom} className="hover:text-primary underline-offset-2 hover:underline">
                        {t('predictionsPage.demoSkipToRoom')}
                      </button>
                    </p>
                  )}
                </div>
              )}

              {/* Public leaderboard */}
              <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="flex items-center gap-2 p-4 sm:p-5 border-b border-border bg-muted/30">
                  <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
                    🌍 {t('predictionsPage.publicLeaderboard')}
                  </h2>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top 5 list */}
                    <div className="space-y-2">
                      {publicData.map((row, i) => (
                        <div
                          key={row.rank}
                          className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <span className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                            {row.rank}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground truncate">{row.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {row.points} {t('predictionsPage.points').toLowerCase()} · {row.accuracy}% {t('predictionsPage.accuracy').toLowerCase()}
                            </p>
                          </div>
                          <span className="text-sm text-muted-foreground shrink-0">
                            {'matches' in row ? (row as any).matches : (row as any).races} {metricsLabel}
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* Metrics cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="rounded-xl border border-border bg-muted/20 p-4 text-center">
                        <Trophy className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-foreground">2,840</p>
                        <p className="text-xs text-muted-foreground">{t('predictionsPage.points')}</p>
                      </div>
                      <div className="rounded-xl border border-border bg-muted/20 p-4 text-center">
                        <Target className="w-6 h-6 text-primary mx-auto mb-1" />
                        <p className="text-2xl font-bold text-foreground">72%</p>
                        <p className="text-xs text-muted-foreground">{t('predictionsPage.accuracy')}</p>
                      </div>
                      <div className="rounded-xl border border-border bg-muted/20 p-4 text-center col-span-2 sm:col-span-1">
                        <BarChart3 className="w-6 h-6 text-success mx-auto mb-1" />
                        <p className="text-2xl font-bold text-foreground">#1</p>
                        <p className="text-xs text-muted-foreground">{t('predictionsPage.worldRank')}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4 text-center">
                    {t('predictionsPage.signInToPlay')} → <Link to="/login" className="text-primary hover:underline">{t('nav.signIn')}</Link>
                  </p>
                </div>
              </div>

              {isFootball ? (
                <>
                  {demoRoomConfig ? (
                    <div ref={roomSectionRef}>
                      <FootballRoomDemo roomConfig={demoRoomConfig} />
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border bg-muted/10 p-8 sm:p-12 text-center">
                      <span className="text-4xl sm:text-5xl block mb-4" aria-hidden>⚽</span>
                      <h3 className="font-display text-lg sm:text-xl font-bold text-foreground mb-2">
                        {t('predictionsPage.demoRoomPlaceholderTitle')}
                      </h3>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                        {t('predictionsPage.demoRoomPlaceholderBody')}
                      </p>
                      <button type="button" onClick={() => setShowCreateModal(true)} className={btnGreen}>
                        {t('dashboard.createRoom')}
                      </button>
                    </div>
                  )}

                  {showAdminDemo && (
                    <div ref={adminSectionRef} className="space-y-3 scroll-mt-24">
                      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 sm:p-5 flex items-start gap-3">
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500/15 text-amber-300 shrink-0">
                          <Coins size={18} />
                        </span>
                        <div className="min-w-0">
                          <h3 className="font-display text-base sm:text-lg font-bold text-foreground">
                            {t('predictionsPage.adminDemoTitle')}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {t('predictionsPage.adminDemoHint')}
                          </p>
                        </div>
                      </div>
                      <FootballRoomDemo adminMode roomConfig={demoRoomConfig} />
                    </div>
                  )}
                </>
              ) : (
              <>
              {/* Room leaderboard (F1) */}
              <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="flex items-center gap-2 p-4 sm:p-5 border-b border-border bg-muted/30">
                  <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
                    👥 {t('predictionsPage.roomLeaderboard')} — {t('predictionsPage.demoRoomName')}
                  </h2>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      {roomData.map((row) => (
                        <div
                          key={row.rank}
                          className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <span className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                            {row.rank}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground truncate">{row.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatPoints(row.points, t)}
                              {'accuracy' in row && ` · ${(row as any).accuracy}% ${t('predictionsPage.accuracy').toLowerCase()}`}
                              {'predCount' in row && ` · ${(row as any).predCount} ${isFootball ? t('roomPage.pred') : t('predictionsPage.races').toLowerCase()}`}
                            </p>
                          </div>
                          <span className="text-sm text-muted-foreground shrink-0">
                            {'matches' in row ? (row as any).matches : 'races' in row ? (row as any).races : (row as any).predCount} {metricsLabel}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="rounded-xl border border-border bg-muted/20 p-4 text-center">
                        <Trophy className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-foreground">{isFootball ? 124 : 186}</p>
                        <p className="text-xs text-muted-foreground">{t('predictionsPage.points')}</p>
                      </div>
                      <div className="rounded-xl border border-border bg-muted/20 p-4 text-center">
                        <Target className="w-6 h-6 text-primary mx-auto mb-1" />
                        <p className="text-2xl font-bold text-foreground">{isFootball ? 78 : 58}%</p>
                        <p className="text-xs text-muted-foreground">{t('predictionsPage.accuracy')}</p>
                      </div>
                      <div className="rounded-xl border border-border bg-muted/20 p-4 text-center col-span-2 sm:col-span-1">
                        <BarChart3 className="w-6 h-6 text-success mx-auto mb-1" />
                        <p className="text-2xl font-bold text-foreground">{isFootball ? 168 : 6}</p>
                        <p className="text-xs text-muted-foreground">{metricsLabel}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4 text-center">
                    {t('predictionsPage.signInToPlay')} → <Link to="/login" className="text-primary hover:underline">{t('nav.signIn')}</Link>
                  </p>
                </div>
              </div>

              {/* Formula 1: room dashboard (rankings + modals) */}
              <div className="rounded-2xl border border-border bg-card overflow-hidden">
                  <div className="p-4 sm:p-6">
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-foreground mb-1">
                      F1 Championship 2026
                    </h2>
                    <p className="text-small sm:text-body-lg text-muted-foreground mb-4 sm:mb-6">
                      {t('roomPage.seasonActive', { count: '9' })}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 mb-6 sm:mb-8">
                      <button
                        onClick={() => setDemoF1PlayOpen(true)}
                        className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-primary text-primary-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1.5 sm:gap-2"
                      >
                        <Play size={14} className="sm:w-4 sm:h-4" /> {t('roomPage.play')}
                      </button>
                      <button
                        onClick={() => setDemoF1BestOfOpen(true)}
                        className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-warning to-destructive text-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1.5 sm:gap-2"
                      >
                        <Crown size={14} className="sm:w-4 sm:h-4" /> {t('roomPage.bestOf')}
                      </button>
                      <button
                        onClick={() => setDemoF1RacesListOpen(true)}
                        className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-success/80 to-primary/80 text-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1.5 sm:gap-2"
                      >
                        <ListOrdered size={14} className="sm:w-4 sm:h-4" /> {t('formula1.racesAndPredictions')}
                      </button>
                    </div>
                    <div className="rounded-xl sm:rounded-2xl border border-border bg-muted/20 p-3 sm:p-4 lg:p-5">
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <h3 className="font-display text-lg sm:text-display-2 font-bold flex items-center gap-1.5 text-foreground">
                          🏆 {t('roomPage.rankings')}
                        </h3>
                        <button className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Filter">
                          <Filter size={14} className="sm:w-4 sm:h-4" />
                        </button>
                      </div>
                      <div className="space-y-2 sm:space-y-2.5">
                        {MOCK_F1_ROOM.map((player, index) => {
                          const isTop3 = index < 3
                          const rankBadge = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null
                          const colors = ['bg-primary', 'bg-destructive', 'bg-success', 'bg-warning', 'bg-chess', 'bg-primary', 'bg-destructive', 'bg-success', 'bg-warning']
                          const color = colors[index % colors.length]
                          return (
                            <div
                              key={player.rank}
                              className={`rounded-lg sm:rounded-xl p-2.5 sm:p-3.5 flex items-center gap-2 sm:gap-3 transition-all hover:bg-muted/30 ${
                                isTop3 ? 'border border-primary/10 bg-card' : 'bg-card/80'
                              }`}
                            >
                              <span className="text-base sm:text-display-2 font-display font-bold w-6 sm:w-7 shrink-0 text-center text-muted-foreground">
                                {rankBadge || (index + 1)}
                              </span>
                              <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full ${color} flex items-center justify-center text-sm font-bold text-primary-foreground shrink-0`}>
                                {player.name.charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-small sm:text-nav truncate text-foreground">{player.name}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-small font-semibold text-primary">{formatPoints(player.points, t)}</span>
                                  <span className="text-small text-muted-foreground">·</span>
                                  <span className="text-small text-muted-foreground">{player.predCount} {t('predictionsPage.races')}</span>
                                </div>
                              </div>
                              <div className="flex gap-1 sm:gap-1.5 shrink-0">
                                <button
                                  onClick={() => setDemoF1GamesPlayer(player)}
                                  className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-md sm:rounded-lg bg-primary/10 text-primary text-small font-medium hover:bg-primary hover:text-primary-foreground transition-all flex items-center gap-1"
                                >
                                  <Gamepad2 size={12} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.games')}</span>
                                </button>
                                <button
                                  onClick={() => setDemoF1StatsPlayer(player)}
                                  className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-md sm:rounded-lg bg-chess/10 text-chess text-small font-medium hover:bg-chess hover:text-chess-foreground transition-all flex items-center gap-1"
                                >
                                  <BarChart3 size={12} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.stats')}</span>
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
              )}

              {/* CTA */}
              <div className="text-center">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
                >
                  <Trophy className="w-5 h-5" /> {t('arena.getStarted')}
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ——— F1 demo modals ——— */}
      {demoF1PlayOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDemoF1PlayOpen(false)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl glass-strong p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-display font-bold text-foreground">Make Your Prediction</h2>
              <button onClick={() => setDemoF1PlayOpen(false)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label={t('stats.close')}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-muted-foreground text-xs sm:text-sm mb-2">Race 2: Shanghai, China · 3/13/2026 · 14:00</p>
            <p className="text-muted-foreground text-xs sm:text-sm mb-4">22 drivers · <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary">upcoming</span></p>
            <p className="text-muted-foreground text-xs sm:text-sm mb-4 text-center">Predict positions 1–10 one by one. Select one driver per place.</p>
            <div className="flex items-center justify-between w-full mb-3">
              <div className="flex gap-1">
                {[1,2,3,4,5,6,7,8,9,10].map((_, i) => (
                  <div key={i} className={`h-1.5 w-3 sm:w-4 rounded-sm flex-shrink-0 ${i === 0 ? 'bg-primary' : 'bg-muted'}`} />
                ))}
              </div>
              <span className="text-muted-foreground font-semibold text-xs sm:text-sm ml-2">1/10</span>
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <button type="button" disabled className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
              <div className="min-w-[120px] text-center px-3 py-2 rounded-xl bg-muted border border-border">
                <span className="font-bold text-foreground text-base">1st place</span>
              </div>
              <button type="button" className="w-10 h-10 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-foreground" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
            </div>
            <div className="mb-3 px-3 py-2 rounded-lg bg-primary/20 border border-primary/30 text-center">
              <p className="text-muted-foreground text-xs">Selected for 1st</p>
              <p className="text-foreground font-semibold text-sm">James Wilson</p>
            </div>
            <p className="text-muted-foreground text-xs sm:text-sm mb-2">{t('formula1.selectOneDriver')}</p>
            <div className="max-h-[200px] overflow-y-auto rounded-xl border border-border bg-muted/20 p-2 space-y-1.5">
              {DEMO_F1_DRIVERS.slice(0, 8).map((driver, i) => (
                <div key={i} className="px-4 py-3 rounded-xl bg-muted/50 border border-border text-sm font-medium text-foreground flex items-center justify-between">
                  <span>{driver}</span>
                  <span className="text-muted-foreground text-xs">(Team {String.fromCharCode(65 + (i % 5))})</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-muted-foreground text-xs mb-1">Progress</p>
              <p className="font-semibold text-sm text-foreground mb-2">1/10 positions filled</p>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-primary h-2 rounded-full transition-all" style={{ width: '10%' }} />
              </div>
            </div>
            <button disabled className="w-full mt-4 py-3 rounded-xl bg-primary/50 text-primary-foreground/80 font-semibold cursor-not-allowed">Update Prediction</button>
            <p className="text-center text-sm text-muted-foreground mt-4">{t('predictionsPage.signInToPlay')} → <Link to="/login" className="text-primary hover:underline" onClick={() => setDemoF1PlayOpen(false)}>{t('nav.signIn')}</Link></p>
            <button onClick={() => setDemoF1PlayOpen(false)} className="w-full mt-3 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90">{t('stats.close')}</button>
          </div>
        </div>
      )}

      {demoF1BestOfOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDemoF1BestOfOpen(false)}>
          <div className="w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl glass-strong p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 gap-2">
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <span className="text-warning text-xl sm:text-2xl flex-shrink-0">🏆</span>
                <h2 className="text-lg sm:text-xl font-display font-bold text-foreground truncate">{t('bestOfF1.title')}</h2>
                <span className="text-warning text-xl sm:text-2xl flex-shrink-0">🏆</span>
              </div>
              <button onClick={() => setDemoF1BestOfOpen(false)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label={t('bestOf.close')}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="rounded-xl py-2 px-4 bg-muted/30 border border-border">
                <h3 className="text-sm sm:text-base font-bold text-warning text-center">{t('bestOfF1.raceLabel', { day: '1', name: 'Melbourne, Australia', date: 'Mar 6, 2026' })}</h3>
              </div>
              {DEMO_F1_BEST_OF.map((row, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 py-2 px-3 sm:p-4 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-foreground font-bold text-sm">{row.playerName.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-bold text-foreground truncate">{row.playerName}</h4>
                      <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 line-clamp-2">{row.predictedText}</p>
                    </div>
                  </div>
                  <span className="font-bold text-foreground text-sm sm:text-base shrink-0">{formatPoints(row.pts, t)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <button onClick={() => setDemoF1BestOfOpen(false)} className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90">{t('bestOf.close')}</button>
            </div>
          </div>
        </div>
      )}

      {demoF1RacesListOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDemoF1RacesListOpen(false)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl glass-strong" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-border">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/20 text-primary"><Gamepad2 className="w-5 h-5" /></span>
                {t('formula1.racesAndPredictions')}
              </h2>
              <button onClick={() => setDemoF1RacesListOpen(false)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {DEMO_F1_RACES.map((race) => (
                <button
                  key={race.id}
                  onClick={() => { setDemoF1RaceDetail(race); setDemoF1RacesListOpen(false); }}
                  className="w-full text-left rounded-xl border border-border bg-card hover:bg-muted/40 p-4 transition-colors"
                >
                  <div className="font-bold text-foreground text-base">🏎️ Race {race.day}: {race.city}, {race.country}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-muted-foreground">
                    <span>{race.date}</span>
                    <span>{race.drivers} drivers</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${race.status === 'completed' ? 'bg-success/20 text-success' : 'bg-primary/20 text-primary'}`}>{race.status}</span>
                    {race.resultsAvailable && (
                      <span className="inline-flex px-2.5 py-0.5 rounded-md bg-primary/15 text-primary font-semibold text-xs">{t('formula1.resultsAvailable')}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-border">
              <button onClick={() => setDemoF1RacesListOpen(false)} className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90">{t('stats.close')}</button>
            </div>
          </div>
        </div>
      )}

      {demoF1RaceDetail && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDemoF1RaceDetail(null)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl glass-strong" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-border">
              <div className="flex justify-between items-center">
                <h2 className="font-bold text-foreground text-lg">Race {demoF1RaceDetail.day}: {demoF1RaceDetail.city}, {demoF1RaceDetail.country}</h2>
                <button onClick={() => setDemoF1RaceDetail(null)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground"><X className="w-5 h-5" /></button>
              </div>
              <div className="text-sm text-muted-foreground mt-1">{demoF1RaceDetail.date} · {demoF1RaceDetail.drivers} drivers · <span className={demoF1RaceDetail.status === 'completed' ? 'text-success' : 'text-primary'}>{demoF1RaceDetail.status}</span></div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-sm text-muted-foreground mb-3">{t('formula1.allPlayersTop10')}</p>
              <div className="space-y-3">
                {MOCK_F1_ROOM.slice(0, 3).map((p) => (
                  <div key={p.rank} className="rounded-xl border border-border bg-muted/20 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">{p.name.charAt(0)}</div>
                      <span className="font-semibold text-foreground">{p.name}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {DEMO_F1_DRIVERS.slice(0, 10).map((d, i) => (
                        <span key={i} className="px-2 py-1 rounded-lg bg-muted text-xs text-foreground">P{i + 1}: {d}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-border">
              <button onClick={() => setDemoF1RaceDetail(null)} className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90">{t('stats.close')}</button>
            </div>
          </div>
        </div>
      )}

      {demoF1GamesPlayer && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDemoF1GamesPlayer(null)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl glass-strong p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start gap-2 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <span className="text-primary-foreground font-bold text-lg">{demoF1GamesPlayer.name.charAt(0)}</span>
                </div>
                <h2 className="text-lg font-display font-bold text-foreground">{demoF1GamesPlayer.name} – {t('roomPage.games')}</h2>
              </div>
              <button onClick={() => setDemoF1GamesPlayer(null)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4 max-h-[50vh]">
              {DEMO_F1_PLAYER_RACES.map((r) => (
                <div key={r.id} className="rounded-xl border border-border bg-muted/20 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="font-semibold text-foreground">Race {r.day}: {r.city}, {r.country}</span>
                    <span className="text-sm text-muted-foreground">{r.date}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === 'completed' ? 'bg-muted text-muted-foreground' : 'bg-primary/20 text-primary'}`}>{r.status === 'completed' ? 'Completed' : 'Upcoming'}</span>
                    <span className="text-warning text-sm font-semibold">{formatPoints(r.pts, t)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Predictions (places 1-10):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.positions.map((driver, i) => (
                      <span key={i} className="px-2 py-1 rounded-lg bg-card border border-border text-xs text-foreground">P{i + 1}: {driver}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <button onClick={() => setDemoF1GamesPlayer(null)} className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90">{t('stats.close')}</button>
            </div>
          </div>
        </div>
      )}

      {demoF1StatsPlayer && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDemoF1StatsPlayer(null)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl glass-strong p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start gap-2 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <span className="text-primary-foreground font-bold text-lg">{demoF1StatsPlayer.name.charAt(0)}</span>
                </div>
                <h2 className="text-lg sm:text-xl font-display font-bold text-foreground">{demoF1StatsPlayer.name}</h2>
              </div>
              <button onClick={() => setDemoF1StatsPlayer(null)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3">
              <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">{t('stats.totalGames')}</span><span className="font-semibold text-foreground">6</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('stats.successRatio')}</span><span className="font-semibold text-foreground">58%</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('stats.points')}</span><span className="font-semibold text-foreground">{demoF1StatsPlayer.points}</span></div>
              </div>
              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <div className="flex items-center gap-2 mb-2"><span className="text-xl">🏆</span><span className="font-semibold text-foreground">Podiums</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Correct P1–P3:</span><span className="font-semibold">4</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('stats.points')}</span><span className="font-semibold">92</span></div>
              </div>
              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <div className="flex items-center gap-2 mb-2"><span className="text-xl">🏎️</span><span className="font-semibold text-foreground">Top 10</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Correct P1–P10:</span><span className="font-semibold">6</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('stats.points')}</span><span className="font-semibold">94</span></div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <button onClick={() => setDemoF1StatsPlayer(null)} className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90">{t('stats.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateRoomModal
          demoMode
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => setShowCreateModal(false)}
          onDemoSuccess={handleDemoRoomCreated}
        />
      )}

      <Footer className="mt-12" />
    </div>
  )
}

export default Showcase
